import React, { useEffect, useRef } from 'react';
import { Bold, Italic, Underline } from 'lucide-react';
import './RichTextEditor.css';

const ALLOWED_TAGS = new Set(['B', 'STRONG', 'I', 'EM', 'U', 'BR', 'P', 'DIV']);

// Strips everything except a small allowlist of inline formatting tags, so
// content pasted from elsewhere can't smuggle in scripts/styles/etc.
function sanitize(html) {
  const doc = document.createElement('div');
  doc.innerHTML = String(html || '');

  function clean(node) {
    [...node.childNodes].forEach((child) => {
      if (child.nodeType === Node.ELEMENT_NODE) {
        if (!ALLOWED_TAGS.has(child.tagName)) {
          const text = document.createTextNode(child.textContent);
          node.replaceChild(text, child);
          return;
        }
        [...child.attributes].forEach((attr) => child.removeAttribute(attr.name));
        clean(child);
      }
    });
  }

  clean(doc);
  return doc.innerHTML;
}

export default function RichTextEditor({
  value,
  onChange,
  placeholder = '',
  readOnly = false,
  rows = 4,
}) {
  const editorRef = useRef(null);

  // Only push external value changes into the DOM when the editor isn't
  // focused, so typing doesn't get clobbered by a prop echoing back.
  useEffect(() => {
    const el = editorRef.current;
    if (!el) return;
    if (document.activeElement === el) return;
    if (el.innerHTML !== (value || '')) {
      el.innerHTML = value || '';
    }
  }, [value]);

  const exec = (command) => {
    if (readOnly) return;
    editorRef.current?.focus();
    document.execCommand(command, false, undefined);
    handleInput();
  };

  const handleInput = () => {
    if (!editorRef.current) return;
    onChange?.(sanitize(editorRef.current.innerHTML));
  };

  const handlePaste = (e) => {
    e.preventDefault();
    const text = e.clipboardData.getData('text/plain');
    document.execCommand('insertText', false, text);
  };

  const isEmpty = !value || value === '<br>';

  return (
    <div className={`rte ${readOnly ? 'rte--readonly' : ''}`}>
      {!readOnly && (
        <div className="rte-toolbar">
          <button type="button" title="Bold" onMouseDown={(e) => e.preventDefault()} onClick={() => exec('bold')}>
            <Bold size={14} />
          </button>
          <button type="button" title="Italic" onMouseDown={(e) => e.preventDefault()} onClick={() => exec('italic')}>
            <Italic size={14} />
          </button>
          <button type="button" title="Underline" onMouseDown={(e) => e.preventDefault()} onClick={() => exec('underline')}>
            <Underline size={14} />
          </button>
        </div>
      )}
      <div
        ref={editorRef}
        className="rte-content"
        style={{ minHeight: `${rows * 1.4}em` }}
        contentEditable={!readOnly}
        suppressContentEditableWarning
        onInput={handleInput}
        onPaste={handlePaste}
        onBlur={handleInput}
        data-placeholder={placeholder}
        data-empty={isEmpty}
      />
    </div>
  );
}
